import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Plus, Loader2, Upload, FileText, Sparkles, Calendar, X
} from 'lucide-react';
import { TimePicker } from '@/components/ui/time-picker';

/**
 * Section header component
 */
export const FormSectionHeader = ({ title, className = '' }) => (
  <div className={`col-span-2 ${className}`}>
    <h3 className="text-sm font-medium text-slate-400 mb-3 border-b border-slate-800 pb-2">
      {title}
    </h3>
  </div>
);

/**
 * AI Document Upload Section
 */
export const AIUploadSection = ({
  doFileInputRef,
  handleDOUpload,
  uploadingDO,
  uploadedDOFile,
  setUploadedDOFile
}) => (
  <div className="col-span-2">
    <div className="p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg border border-green-500/30 mb-4">
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="h-5 w-5 text-green-400" />
        <h3 className="font-medium text-white">Upload Delivery Order</h3>
      </div>
      <p className="text-slate-400 text-sm mb-3">Upload a DO image to auto-fill fields with AI</p>
      <div className="flex items-center gap-3">
        <input 
          type="file" 
          ref={doFileInputRef} 
          onChange={handleDOUpload} 
          accept=".pdf,.png,.jpg,.jpeg" 
          className="hidden" 
        />
        <Button
          type="button"
          onClick={() => doFileInputRef.current?.click()}
          disabled={uploadingDO}
          variant="outline"
          className="border-green-500/50 hover:border-green-400 hover:bg-green-500/10"
          data-testid="upload-export-do-btn"
        >
          {uploadingDO ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
          ) : (
            <><Upload className="h-4 w-4 mr-2" />Upload DO</>
          )}
        </Button>
        {uploadedDOFile && !uploadingDO && (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <FileText className="h-4 w-4" />
            <span>{uploadedDOFile.name}</span>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={() => setUploadedDOFile(null)} 
              className="h-6 w-6 p-0 text-slate-400 hover:text-white"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      <p className="text-slate-500 text-xs mt-2">Supports PDF, PNG, JPG files</p>
    </div>
  </div>
);

/**
 * Booking Information Section
 */
export const BookingInfoSection = ({
  formData,
  handleInputChange,
  handleSSLSelect,
  ssls,
  masterData
}) => (
  <>
    <FormSectionHeader title="Booking Information" />
    
    <div className="space-y-2">
      <Label>BK# (MBL Number) *</Label>
      <Input
        value={formData.mbl_number}
        onChange={(e) => handleInputChange('mbl_number', e.target.value.toUpperCase())}
        className="bg-slate-950 border-slate-800 font-mono"
        placeholder="Full MBL number"
        data-testid="mbl-number-input"
      />
    </div>

    <div className="space-y-2">
      <Label>Booking #</Label>
      <Input
        value={formData.booking_number}
        onChange={(e) => handleInputChange('booking_number', e.target.value.toUpperCase())}
        className="bg-slate-950 border-slate-800 font-mono"
        placeholder="Booking reference"
      />
    </div>

    <div className="space-y-2">
      <Label>Container #</Label>
      <Input
        value={formData.container_number}
        onChange={(e) => handleInputChange('container_number', e.target.value.toUpperCase())}
        className="bg-slate-950 border-slate-800 font-mono"
        placeholder="e.g., MSCU1234567"
      />
    </div>

    <div className="space-y-2">
      <Label>Size</Label>
      <Select value={formData.container_size} onValueChange={(v) => handleInputChange('container_size', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select size" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          {masterData.container_types?.length > 0 ? (
            masterData.container_types.map(ct => (
              <SelectItem key={ct.id} value={ct.code} className="text-white">{ct.code} - {ct.name}</SelectItem>
            ))
          ) : (
            <>
              <SelectItem value="20GP" className="text-white">20GP (Standard)</SelectItem>
              <SelectItem value="20RF" className="text-white">20RF (Reefer)</SelectItem>
              <SelectItem value="40GP" className="text-white">40GP (Standard)</SelectItem>
              <SelectItem value="40HQ" className="text-white">40HQ (High Cube)</SelectItem>
              <SelectItem value="40RF" className="text-white">40RF (Reefer)</SelectItem>
              <SelectItem value="45HQ" className="text-white">45HQ (High Cube)</SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>SSL (Steamship Line)</Label>
      <Select value={formData.ssl_id} onValueChange={handleSSLSelect}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select SSL">
            {formData.ssl || 'Select SSL'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          {ssls.length === 0 ? (
            <SelectItem value="_none" disabled className="text-slate-500">No SSLs configured</SelectItem>
          ) : (
            ssls.map(ssl => (
              <SelectItem key={ssl.id} value={ssl.id} className="text-white">
                {ssl.code || ssl.name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Vessel Name</Label>
      <Input
        value={formData.vessel_name}
        onChange={(e) => handleInputChange('vessel_name', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="e.g., MSC GULSUN"
        data-testid="vessel-name-input"
      />
    </div>
  </>
);

/**
 * Customer & Shipper Section
 */
export const CustomerShipperSection = ({
  formData,
  handleInputChange,
  handleCustomerSelect,
  customers,
  recentCustomerIds = []
}) => {
  const recentCustomers = customers.filter(c => recentCustomerIds.includes(c.id));
  const otherCustomers = customers.filter(c => !recentCustomerIds.includes(c.id));

  return (
  <>
    <FormSectionHeader title="Customer & Shipper" className="mt-4" />

    <div className="space-y-2">
      <Label>Customer</Label>
      <Select value={formData.customer_id} onValueChange={handleCustomerSelect}>
        <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="export-customer-select">
          <SelectValue placeholder="Select customer">
            {formData.customer_name || 'Select customer'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-[260px]">
          <SelectItem value="add_new" className="text-blue-400 font-medium">
            <span className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Customer
            </span>
          </SelectItem>
          {recentCustomers.length > 0 && (
            <>
              <div className="px-2 py-1 text-xs text-slate-500 font-medium">Recent</div>
              {recentCustomers.map(customer => (
                <SelectItem key={customer.id} value={customer.id} className="text-white">
                  {customer.name}
                </SelectItem>
              ))}
              <div className="border-t border-slate-800 my-1" />
              <div className="px-2 py-1 text-xs text-slate-500 font-medium">All Customers</div>
            </>
          )}
          {otherCustomers.map(customer => (
            <SelectItem key={customer.id} value={customer.id} className="text-white">
              {customer.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Shipper</Label>
      <Input
        value={formData.shipper_name}
        onChange={(e) => handleInputChange('shipper_name', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="Shipper name"
      />
    </div>
  </>
  );
};

/**
 * Pickup Location Section with Address Selector
 */
export const PickupLocationSection = ({
  formData,
  setFormData,
  handleInputChange,
  customers,
  showAddAddressModal,
  setShowAddAddressModal,
  newAddress,
  setNewAddress,
  handleAddAddress,
  addingAddress
}) => {
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  const customerAddresses = selectedCustomer?.addresses || [];

  return (
    <>
      <FormSectionHeader title="Pickup Location" className="mt-4" />

      {/* Customer Address Selector */}
      {formData.customer_id && (
        <div className="col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Select from Saved Addresses</Label>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowAddAddressModal(true)} 
              className="text-blue-400 hover:text-blue-300 h-auto p-0"
            >
              <Plus className="h-3 w-3 mr-1" />Add New Address
            </Button>
          </div>
          {customerAddresses.length > 0 ? (
            <Select 
              value={formData.address_id || undefined}
              onValueChange={(addrId) => {
                const addr = customerAddresses.find(a => a.id === addrId);
                if (addr) {
                  setFormData(prev => ({
                    ...prev,
                    address_id: addrId,
                    pickup_address: addr.address || '',
                    pickup_city: addr.city || '',
                    pickup_state: addr.state || '',
                    pickup_zip: addr.zip_code || addr.zip || '',
                    pickup_contact: addr.contact_name || '',
                    pickup_phone: addr.contact_phone || ''
                  }));
                }
              }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-800">
                <SelectValue placeholder="Choose a saved address or enter manually below" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {customerAddresses.map(addr => (
                  <SelectItem key={addr.id} value={addr.id} className="text-white">
                    <div className="flex flex-col">
                      <span className="font-medium">{addr.label || 'Address'}{addr.is_default ? ' (Default)' : ''}</span>
                      <span className="text-xs text-slate-400">{addr.address}, {addr.city}, {addr.state}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-sm text-slate-400">
              No saved addresses for {selectedCustomer?.name}. Click "Add New Address" above or enter manually below.
            </div>
          )}
        </div>
      )}

      {/* Add New Address Inline Form */}
      {showAddAddressModal && formData.customer_id && (
        <AddAddressInlineForm
          customerName={selectedCustomer?.name}
          newAddress={newAddress}
          setNewAddress={setNewAddress}
          handleAddAddress={handleAddAddress}
          addingAddress={addingAddress}
          onClose={() => setShowAddAddressModal(false)}
        />
      )}

      {/* Manual Address Fields */}
      <div className="col-span-2 space-y-2">
        <Label>Pickup Address</Label>
        <Input
          value={formData.pickup_address}
          onChange={(e) => handleInputChange('pickup_address', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="Street address"
        />
      </div>

      <div className="space-y-2">
        <Label>City</Label>
        <Input
          value={formData.pickup_city}
          onChange={(e) => handleInputChange('pickup_city', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="City"
        />
      </div>

      <div className="space-y-2">
        <Label>State</Label>
        <Input
          value={formData.pickup_state}
          onChange={(e) => handleInputChange('pickup_state', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="State"
        />
      </div>

      <div className="space-y-2">
        <Label>ZIP</Label>
        <Input
          value={formData.pickup_zip}
          onChange={(e) => handleInputChange('pickup_zip', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="ZIP code"
        />
      </div>

      <div className="space-y-2">
        <Label>Contact Name</Label>
        <Input
          value={formData.pickup_contact}
          onChange={(e) => handleInputChange('pickup_contact', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="Contact name"
        />
      </div>

      <div className="space-y-2">
        <Label>Contact Phone</Label>
        <Input
          value={formData.pickup_phone}
          onChange={(e) => handleInputChange('pickup_phone', e.target.value)}
          className="bg-slate-950 border-slate-800"
          placeholder="(555) 123-4567"
        />
      </div>
    </>
  );
};

/**
 * Inline Add Address Form
 */
const AddAddressInlineForm = ({
  customerName,
  newAddress,
  setNewAddress,
  handleAddAddress,
  addingAddress,
  onClose
}) => (
  <div className="col-span-2 p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
    <div className="flex items-center justify-between">
      <Label className="text-slate-300 font-medium">
        Add New Address for {customerName}
      </Label>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        onClick={onClose} 
        className="text-slate-400 h-auto p-1 hover:text-white"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input 
        value={newAddress.label} 
        onChange={(e) => setNewAddress({...newAddress, label: e.target.value})} 
        placeholder="Label (e.g., Warehouse, Main Office)" 
        className="bg-slate-950 border-slate-700 col-span-2" 
      />
      <Input 
        value={newAddress.address} 
        onChange={(e) => setNewAddress({...newAddress, address: e.target.value})} 
        placeholder="Street Address *" 
        className="bg-slate-950 border-slate-700 col-span-2" 
      />
      <Input 
        value={newAddress.city} 
        onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} 
        placeholder="City *" 
        className="bg-slate-950 border-slate-700" 
      />
      <Input 
        value={newAddress.state} 
        onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} 
        placeholder="State *" 
        className="bg-slate-950 border-slate-700" 
      />
      <Input 
        value={newAddress.zip_code} 
        onChange={(e) => setNewAddress({...newAddress, zip_code: e.target.value})} 
        placeholder="ZIP Code" 
        className="bg-slate-950 border-slate-700" 
      />
      <Input 
        value={newAddress.contact_name} 
        onChange={(e) => setNewAddress({...newAddress, contact_name: e.target.value})} 
        placeholder="Contact Name" 
        className="bg-slate-950 border-slate-700" 
      />
      <Input 
        value={newAddress.contact_phone} 
        onChange={(e) => setNewAddress({...newAddress, contact_phone: e.target.value})} 
        placeholder="Contact Phone" 
        className="bg-slate-950 border-slate-700" 
      />
      <div className="col-span-2">
        <Label className="text-slate-400 text-xs mb-1 block">Receiving Hours</Label>
        <div className="flex items-center gap-2">
          <TimePicker value={newAddress.receiving_hours_from} onChange={(t) => setNewAddress({...newAddress, receiving_hours_from: t})} placeholder="From" className="w-32" />
          <span className="text-slate-400 text-xs">to</span>
          <TimePicker value={newAddress.receiving_hours_to} onChange={(t) => setNewAddress({...newAddress, receiving_hours_to: t})} placeholder="To" className="w-32" />
        </div>
      </div>
      <Input 
        value={newAddress.remark} 
        onChange={(e) => setNewAddress({...newAddress, remark: e.target.value})} 
        placeholder="Remark (gate code, dock, etc.)" 
        className="bg-slate-950 border-slate-700 col-span-2" 
      />
      <div className="flex items-center gap-2">
        <Checkbox 
          checked={newAddress.is_default}
          onCheckedChange={(checked) => setNewAddress({...newAddress, is_default: checked})}
        />
        <span className="text-sm text-slate-400">Set as default</span>
      </div>
    </div>
    <div className="flex justify-end gap-2 pt-2">
      <Button 
        type="button" 
        variant="outline" 
        size="sm"
        onClick={onClose} 
        className="border-slate-600"
      >
        Cancel
      </Button>
      <Button 
        type="button" 
        size="sm"
        onClick={handleAddAddress}
        disabled={addingAddress}
        className="bg-blue-600 hover:bg-blue-500"
      >
        {addingAddress ? 'Saving...' : 'Save Address'}
      </Button>
    </div>
  </div>
);

/**
 * Terminal & Equipment Section
 */
export const TerminalEquipmentSection = ({
  formData,
  handleInputChange,
  masterData
}) => (
  <>
    <FormSectionHeader title="Terminal & Equipment" className="mt-4" />

    <div className="space-y-2">
      <Label>Pickup Terminal</Label>
      <Select value={formData.pickup_terminal_id} onValueChange={(v) => handleInputChange('pickup_terminal_id', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select pickup terminal" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
          {(!masterData.terminals || masterData.terminals.length === 0) ? (
            <SelectItem value="none" disabled className="text-slate-500">No terminals available</SelectItem>
          ) : (
            masterData.terminals.map(terminal => (
              <SelectItem key={terminal.id} value={terminal.id} className="text-white">{terminal.name}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Return Terminal</Label>
      <Select value={formData.return_terminal_id} onValueChange={(v) => handleInputChange('return_terminal_id', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select return terminal" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
          {(!masterData.terminals || masterData.terminals.length === 0) ? (
            <SelectItem value="none" disabled className="text-slate-500">No terminals available</SelectItem>
          ) : (
            masterData.terminals.map(terminal => (
              <SelectItem key={terminal.id} value={terminal.id} className="text-white">{terminal.name}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Yard</Label>
      <Select value={formData.yard_id} onValueChange={(v) => handleInputChange('yard_id', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select yard" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          {(!masterData.yards || masterData.yards.length === 0) ? (
            <SelectItem value="none" disabled className="text-slate-500">No yards available</SelectItem>
          ) : (
            masterData.yards.map(yard => (
              <SelectItem key={yard.id} value={yard.id} className="text-white">{yard.name}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Chassis Type</Label>
      <Select value={formData.chassis_type} onValueChange={(v) => handleInputChange('chassis_type', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue placeholder="Select chassis type" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          {masterData.chassis_types?.length > 0 ? (
            masterData.chassis_types.map(ct => (
              <SelectItem key={ct.id} value={ct.code || ct.name} className="text-white">{ct.name}</SelectItem>
            ))
          ) : (
            <>
              <SelectItem value="OWN" className="text-white">OWN</SelectItem>
              <SelectItem value="POOL" className="text-white">POOL</SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Chassis Number</Label>
      <Input
        value={formData.chassis_number}
        onChange={(e) => handleInputChange('chassis_number', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="Chassis #"
      />
    </div>
  </>
);

/**
 * Destination Section
 */
export const DestinationSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <FormSectionHeader title="Destination" className="mt-4" />

    <div className="space-y-2">
      <Label>Destination Port</Label>
      <Input
        value={formData.destination_port}
        onChange={(e) => handleInputChange('destination_port', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="e.g., Shanghai, Rotterdam"
      />
    </div>

    <div className="space-y-2">
      <Label>Destination Terminal</Label>
      <Input
        value={formData.destination_terminal}
        onChange={(e) => handleInputChange('destination_terminal', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="Destination terminal"
      />
    </div>
  </>
);

/**
 * Important Dates Section
 */
export const ImportantDatesSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <FormSectionHeader title="Important Dates" className="mt-4" />

    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-red-400" />
        Cutoff Date
      </Label>
      <Input
        type="date"
        value={formData.cutoff_date}
        onChange={(e) => handleInputChange('cutoff_date', e.target.value)}
        className="bg-slate-950 border-slate-800"
        data-testid="cutoff-date-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-amber-400" />
        ERD (Early Return Date)
      </Label>
      <Input
        type="date"
        value={formData.erd}
        onChange={(e) => handleInputChange('erd', e.target.value)}
        className="bg-slate-950 border-slate-800"
        data-testid="erd-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-green-400" />
        Load Rdy
      </Label>
      <Input
        type="date"
        value={formData.load_rdy}
        onChange={(e) => handleInputChange('load_rdy', e.target.value)}
        className="bg-slate-950 border-slate-800"
        data-testid="load-rdy-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-blue-400" />
        Load Return
      </Label>
      <Input
        type="date"
        value={formData.load_return}
        onChange={(e) => handleInputChange('load_return', e.target.value)}
        className="bg-slate-950 border-slate-800"
        data-testid="load-return-input"
      />
    </div>
  </>
);

/**
 * Assignment & Cargo Section
 */
export const AssignmentCargoSection = ({
  formData,
  handleInputChange,
  masterData,
  isEdit,
  EXPORT_STATUSES
}) => (
  <>
    <FormSectionHeader title={isEdit ? 'Assignment & Cargo' : 'Cargo Details'} className="mt-4" />

    {/* Driver - only show when editing, not when adding new */}
    {isEdit && (
      <div className="space-y-2">
        <Label>Driver</Label>
        <Select value={formData.driver_id} onValueChange={(v) => handleInputChange('driver_id', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800">
            <SelectValue placeholder="Select driver">
              {formData.driver_name || 'Select driver'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
            <SelectItem value="none" className="text-slate-400">-- No driver --</SelectItem>
            {(masterData.drivers || []).map(driver => (
              <SelectItem key={driver.id} value={driver.id} className="text-white">{driver.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    <div className="space-y-2">
      <Label>Status</Label>
      <Select value={formData.status} onValueChange={(v) => handleInputChange('status', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800">
          {EXPORT_STATUSES.map(status => (
            <SelectItem key={status} value={status}>{status}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label>Weight (lbs)</Label>
      <Input
        type="number"
        value={formData.weight}
        onChange={(e) => handleInputChange('weight', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="0"
      />
    </div>

    <div className="space-y-2">
      <Label>Commodity</Label>
      <Input
        value={formData.commodity}
        onChange={(e) => handleInputChange('commodity', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="Cargo description"
      />
    </div>
  </>
);

/**
 * Multiple Stop Section
 */
export const MultipleStopSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <div className="col-span-2 mt-4 flex items-center gap-2">
      <Checkbox 
        id="is_multiple_stop" 
        checked={formData.is_multiple_stop} 
        onCheckedChange={(checked) => handleInputChange('is_multiple_stop', checked)} 
        className="border-slate-600" 
      />
      <Label htmlFor="is_multiple_stop" className="text-orange-400 font-medium cursor-pointer">Multiple Stop Load</Label>
    </div>

    {formData.is_multiple_stop && (
      <>
        <div className="col-span-2 mt-2">
          <h3 className="text-sm font-medium text-orange-400 mb-3">Secondary Stop</h3>
        </div>
        <div className="space-y-2">
          <Label>Address</Label>
          <Input value={formData.secondary_address} onChange={(e) => handleInputChange('secondary_address', e.target.value)} className="bg-slate-950 border-slate-800" placeholder="Secondary address" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-2">
            <Label>City</Label>
            <Input value={formData.secondary_city} onChange={(e) => handleInputChange('secondary_city', e.target.value)} className="bg-slate-950 border-slate-800" />
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Input value={formData.secondary_state} onChange={(e) => handleInputChange('secondary_state', e.target.value)} className="bg-slate-950 border-slate-800" />
          </div>
          <div className="space-y-2">
            <Label>ZIP</Label>
            <Input value={formData.secondary_zip} onChange={(e) => handleInputChange('secondary_zip', e.target.value)} className="bg-slate-950 border-slate-800" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Contact</Label>
          <Input value={formData.secondary_contact} onChange={(e) => handleInputChange('secondary_contact', e.target.value)} className="bg-slate-950 border-slate-800" placeholder="Contact name" />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input value={formData.secondary_phone} onChange={(e) => handleInputChange('secondary_phone', e.target.value)} className="bg-slate-950 border-slate-800" placeholder="Phone" />
        </div>
      </>
    )}
  </>
);

/**
 * Additional Info Section
 */
export const AdditionalInfoSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <div className="col-span-2 space-y-2">
      <Label>Additional Client Info</Label>
      <Textarea 
        value={formData.additional_client_info} 
        onChange={(e) => handleInputChange('additional_client_info', e.target.value)} 
        placeholder="Any additional client information..." 
        className="bg-slate-950 border-slate-800 min-h-[60px]" 
      />
    </div>

    <div className="space-y-2">
      <Label>Forwarder Reference</Label>
      <Input 
        value={formData.forwarder_reference} 
        onChange={(e) => handleInputChange('forwarder_reference', e.target.value)} 
        placeholder="Forwarder reference number" 
        className="bg-slate-950 border-slate-800" 
      />
    </div>
  </>
);

/**
 * Fees Section
 */
export const FeesSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <FormSectionHeader title="Fees" className="mt-4" />

    <div className="space-y-2">
      <Label>Pier Pass (PP)</Label>
      <div className="flex items-center gap-2">
        <Input 
          type="number" 
          value={formData.pier_pass} 
          onChange={(e) => handleInputChange('pier_pass', parseFloat(e.target.value) || 0)} 
          className="bg-slate-950 border-slate-800 w-24" 
        />
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={formData.pier_pass_paid} 
            onCheckedChange={(c) => handleInputChange('pier_pass_paid', c)} 
            className="border-slate-600" 
          />
          <Label className="text-slate-400 text-xs">Paid</Label>
        </div>
      </div>
    </div>

    <div className="space-y-2">
      <Label>CTF Charge</Label>
      <div className="flex items-center gap-2">
        <Input 
          type="number" 
          value={formData.ctf_charge} 
          onChange={(e) => handleInputChange('ctf_charge', parseFloat(e.target.value) || 0)} 
          className="bg-slate-950 border-slate-800 w-24" 
        />
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={formData.ctf_paid} 
            onCheckedChange={(c) => handleInputChange('ctf_paid', c)} 
            className="border-slate-600" 
          />
          <Label className="text-slate-400 text-xs">Paid</Label>
        </div>
      </div>
    </div>

    {(formData.pier_pass_paid || formData.ctf_paid) && (
      <div className="space-y-2">
        <Label>PP/CTF Paid By</Label>
        <Select value={formData.fees_paid_by || "none"} onValueChange={(v) => handleInputChange('fees_paid_by', v === "none" ? "" : v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue placeholder="Select who paid" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="none" className="text-slate-400">Select</SelectItem>
            <SelectItem value="Customer" className="text-white">Customer</SelectItem>
            <SelectItem value="Company" className="text-white">Company</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}

    <div className="space-y-2">
      <Label className="text-amber-400">OW (Overweight)</Label>
      <div className="flex items-center gap-2">
        <Input 
          type="number" 
          value={formData.overweight_fee} 
          onChange={(e) => handleInputChange('overweight_fee', parseFloat(e.target.value) || 0)} 
          className="bg-slate-950 border-amber-500/50 w-24" 
        />
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={formData.is_overweight} 
            onCheckedChange={(c) => handleInputChange('is_overweight', c)} 
            className="border-amber-500" 
          />
          <Label className="text-amber-400 text-xs">OW</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={formData.is_hazmat} 
            onCheckedChange={(c) => handleInputChange('is_hazmat', c)} 
            className="border-orange-500" 
            data-testid="export-hazmat-checkbox"
          />
          <Label className={`text-xs ${formData.is_hazmat ? 'text-orange-400 font-bold' : 'text-orange-400/60'}`}>HZ</Label>
        </div>
      </div>
    </div>
  </>
);

/**
 * Remarks Section
 */
export const RemarksSection = ({
  formData,
  handleInputChange
}) => (
  <>
    <div className="col-span-2 mt-4 flex items-center gap-2">
      <Checkbox 
        id="is_important" 
        checked={formData.is_important} 
        onCheckedChange={(checked) => handleInputChange('is_important', checked)} 
        className="border-slate-600" 
      />
      <Label htmlFor="is_important" className="text-red-400 font-medium">IMPORTANT SHIPMENT</Label>
    </div>

    <div className="col-span-2 space-y-2">
      <Label>Remarks</Label>
      <Textarea 
        value={formData.remarks} 
        onChange={(e) => handleInputChange('remarks', e.target.value)} 
        placeholder="Additional notes..." 
        className="bg-slate-950 border-slate-800 min-h-[80px]" 
      />
    </div>

    <div className="col-span-2 space-y-2">
      <Label>Special Instructions</Label>
      <Textarea
        value={formData.special_instructions}
        onChange={(e) => handleInputChange('special_instructions', e.target.value)}
        className="bg-slate-950 border-slate-800"
        placeholder="Any special handling instructions..."
        rows={3}
      />
    </div>
  </>
);

export default {
  FormSectionHeader,
  AIUploadSection,
  BookingInfoSection,
  CustomerShipperSection,
  PickupLocationSection,
  TerminalEquipmentSection,
  DestinationSection,
  ImportantDatesSection,
  AssignmentCargoSection,
  MultipleStopSection,
  AdditionalInfoSection,
  FeesSection,
  RemarksSection
};
